import React, { useState, useEffect } from 'react';
import { AdminPageLayout } from '@/components/admin/shared/AdminPageLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InviteUserModal } from '@/components/modals/InviteUserModal';
import { EditUserModal } from '@/components/modals/EditUserModal';
import { useUsers, UserProfile } from '@/hooks/useUsers';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { apiClient } from '@/services/api';
import { useAuth } from '@/components/AuthProvider';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { 
  UserPlus, 
  Users, 
  Shield, 
  Clock, 
  MoreHorizontal,
  Edit,
  Trash2,
  Lock,
  Unlock,
  RefreshCw
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface ClientProject {
  id: string;
  name: string;
  status?: string;
}

export function AdminUsers() {
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [userToDelete, setUserToDelete] = useState<UserProfile | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [clientProjects, setClientProjects] = useState<ClientProject[]>([]);
  const { user: currentUser } = useAuth();
  
  const { users, isLoading, error, refetch, invalidateUsers, updateUser, deleteUser } = useUsers({
    role: roleFilter && roleFilter !== 'all' ? roleFilter : undefined,
    clientProjectId: projectFilter && projectFilter !== 'all' ? projectFilter : undefined,
  });

  // Fetch client projects
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await apiClient.get('/clients?status=active');
        if (response.data?.success && response.data?.data?.clients) {
          setClientProjects(response.data.data.clients);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
    };
    fetchProjects();
  }, []);

  const filteredUsers = users.filter(user => 
    user.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEditUser = (user: UserProfile) => {
    setSelectedUser(user);
    setIsEditModalOpen(true);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    setIsDeleting(true);
    try {
      await deleteUser.mutateAsync(userToDelete.id);
      setUserToDelete(null);
    } catch (error) {
      console.error('Error deleting user:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const getRoleColor = (role: string) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-800',
      developer: 'bg-blue-100 text-blue-800',
      viewer: 'bg-gray-100 text-gray-800'
    };
    return colors[role as keyof typeof colors] || colors.viewer;
  };


  const getInitials = (name: string | null) => {
    if (!name) return '??';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const roleCounts = {
    admin: users.filter(u => u.role === 'admin').length,
    developer: users.filter(u => u.role === 'developer').length,
    viewer: users.filter(u => u.role === 'viewer').length,
    total: users.length
  };

  return (
    <AdminPageLayout
      title="Gestión de Usuarios"
      subtitle={`${users.length} usuarios en el sistema`}
      actions={
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
          <Button onClick={() => setIsInviteModalOpen(true)}>
            <UserPlus className="w-4 h-4 mr-2" />
            Invitar Usuario
          </Button>
        </div>
      }
      filters={
        <div className="flex gap-3">
          <Input
            placeholder="Buscar usuarios..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-sm"
          />
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Todos los roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los roles</SelectItem>
              <SelectItem value="admin">Administradores</SelectItem>
              <SelectItem value="developer">Desarrolladores</SelectItem>
              <SelectItem value="viewer">Visualizadores</SelectItem>
            </SelectContent>
          </Select>
          <Select value={projectFilter} onValueChange={setProjectFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Todos los proyectos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los proyectos</SelectItem>
              {clientProjects.map(project => (
                <SelectItem key={project.id} value={project.id || `project-${project.name}`}>
                  {project.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      }
    >
      {/* Estadísticas rápidas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-primary">{roleCounts.total}</div>
            <div className="text-sm text-muted-foreground">Total Usuarios</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">{roleCounts.admin}</div>
            <div className="text-sm text-muted-foreground">Administradores</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{roleCounts.developer}</div>
            <div className="text-sm text-muted-foreground">Desarrolladores</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-gray-600">{roleCounts.viewer}</div>
            <div className="text-sm text-muted-foreground">Visualizadores</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabla de usuarios */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="text-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4" />
              <p>Cargando usuarios...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-500 mb-4">Error cargando usuarios: {error.message}</p>
              <Button onClick={() => refetch()}>Reintentar</Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-medium">Usuario</th>
                    <th className="text-left p-4 font-medium">Rol</th>
                    <th className="text-left p-4 font-medium">Proyecto Cliente</th>
                    <th className="text-left p-4 font-medium">Último Acceso</th>
                    <th className="text-left p-4 font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-8">
                        No se encontraron usuarios. Invita al primer usuario para comenzar.
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((user) => (
                      <tr key={user.id} className="border-b hover:bg-muted/30">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            {user.avatarUrl ? (
                              <img
                                src={user.avatarUrl}
                                alt={user.fullName || 'Usuario'}
                                className="w-10 h-10 rounded-full object-cover"
                                onError={(e) => {
                                  // Fallback to initials if image fails to load
                                  e.currentTarget.style.display = 'none';
                                  const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                                  if (fallback) fallback.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div
                              className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-medium"
                              style={{ display: user.avatarUrl ? 'none' : 'flex' }}
                            >
                              {getInitials(user.fullName || user.email.split('@')[0])}
                            </div>
                            <div>
                              <div className="font-medium">{user.fullName || 'Sin nombre'}</div>
                              <div className="text-sm text-muted-foreground">{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <Badge className={getRoleColor(user.role)}>
                            {user.role === 'admin' && 'Administrador'}
                            {user.role === 'developer' && 'Desarrollador'}
                            {user.role === 'viewer' && 'Visualizador'}
                          </Badge>
                        </td>
                        <td className="p-4">
                          <div className="text-sm">
                            {user.clientProjectId ? (
                              clientProjects.find(p => p.id === user.clientProjectId)?.name || 'Proyecto desconocido'
                            ) : (
                              <span className="text-muted-foreground">Sin proyecto</span>
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="text-sm">
                            {user.lastSignInAt ? (
                              new Date(user.lastSignInAt).toLocaleDateString('es-ES', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            ) : (
                              <span className="text-muted-foreground">Nunca</span>
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              <DropdownMenuItem onClick={() => handleEditUser(user)}>
                                <Edit className="w-4 h-4 mr-2" />
                                Editar
                              </DropdownMenuItem>
                              {currentUser?.id !== user.id && (
                                <DropdownMenuItem 
                                  className="text-red-600"
                                  onClick={() => setUserToDelete(user)}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Eliminar
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de invitación */}
      <InviteUserModal 
        isOpen={isInviteModalOpen} 
        onClose={() => setIsInviteModalOpen(false)}
        onSuccess={() => {
          invalidateUsers();
          refetch();
        }}
      />

      {/* Modal de edición */}
      <EditUserModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedUser(null);
        }}
        onSuccess={() => {
          invalidateUsers();
          refetch();
        }}
        user={selectedUser}
      />

      {/* Diálogo de confirmación para eliminar */}
      <ConfirmDialog
        isOpen={!!userToDelete}
        onClose={() => setUserToDelete(null)}
        onConfirm={handleDeleteUser}
        title="¿Eliminar usuario?"
        description={`¿Estás seguro de que deseas eliminar a ${userToDelete?.fullName || userToDelete?.email}? Esta acción no se puede deshacer y el usuario perderá acceso permanentemente al sistema.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        variant="destructive"
        isLoading={isDeleting}
      />
    </AdminPageLayout>
  );
}